import "./qa-premium.css";
import { useRoute } from "wouter";
import { trpc } from "@/utils/trpc";
import { formatDate, QaCrumb } from "./qa-shared";

const ACTION_META: Record<string, { icon: string; bg: string; fg: string }> = {
  qa_visit_scheduled: { icon: "ti-calendar-plus", bg: "var(--p-pale)", fg: "var(--p)" },
  qa_visit_cancelled: { icon: "ti-x", bg: "var(--red-pale)", fg: "var(--red)" },
  qa_questionnaire_submitted: { icon: "ti-send", bg: "var(--blue-pale)", fg: "var(--blue)" },
  qa_review_started: { icon: "ti-eye", bg: "var(--amber-pale)", fg: "var(--amber)" },
  qa_flag_set: { icon: "ti-flag", bg: "var(--amber-pale)", fg: "var(--amber)" },
  qa_questionnaire_returned: { icon: "ti-arrow-back-up", bg: "var(--amber-pale)", fg: "var(--amber)" },
  qa_assessment_submitted: { icon: "ti-clipboard-check", bg: "var(--p-pale)", fg: "var(--p-mid)" },
  qa_report_generated: { icon: "ti-file-text", bg: "var(--p-pale)", fg: "var(--p)" },
  qa_report_approved: { icon: "ti-circle-check", bg: "var(--green-pale)", fg: "var(--green)" },
  qa_report_rejected: { icon: "ti-arrow-back-up", bg: "var(--red-pale)", fg: "var(--red)" },
};

function initials(name?: string | null) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
}
function dayKey(d: string | Date) {
  const date = new Date(d);
  return date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}
function timeOf(d: string | Date) {
  return new Date(d).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
}

const DETAIL_LABELS: Record<string, string> = {
  reason: "Reason", flag: "Flag", answered: "Answered", visitNumber: "Visit", questionId: "Question", versionId: "Version",
};

export default function QaAuditTrail() {
  const [, params] = useRoute("/qa/visits/:id/audit");
  const visitId = params?.id ? Number(params.id) : undefined;

  const { data: visit } = trpc.qa.visits.getById.useQuery({ id: visitId! }, { enabled: !!visitId });
  const { data: logs = [], isLoading } = trpc.qa.audit.list.useQuery({ visitId: visitId! }, { enabled: !!visitId });

  let lastDay = "";

  return (
    <div className="qa-premium full">
      <div className="audit-header">
        <div className="audit-header-inner">
          <div>
            <QaCrumb backTo={`/qa/visits/${visitId}`} items={[{ label: "Visits", to: "/qa/visits" }, { label: visit?.visitNumber ?? `Visit #${visitId}`, to: `/qa/visits/${visitId}`, mono: true }, { label: "Audit Trail" }]} />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: "#1a1530", lineHeight: 1.2, marginTop: 8 }}>Audit Trail</h1>
            <p style={{ fontSize: 13.5, color: "#6b6386", marginTop: 4 }}>Chronological log of every action taken on this visit.</p>
          </div>
        </div>
      </div>

      <div className="wrap" style={{ paddingTop: 24 }}>
        {visit && (
          <div className="cmeta" style={{ marginBottom: 24 }}>
            <span className="num">{visit.visitNumber}</span>
            <span><b>{visit.siteName ?? `Site #${visit.siteId}`}</b></span>
            <span>Visit date: <b>{formatDate(visit.visitDate)}</b></span>
          </div>
        )}

        {isLoading ? (
          <div className="loading"><span className="spin" /></div>
        ) : logs.length === 0 ? (
          <div className="empty"><i className="ti ti-history" />No activity recorded yet.</div>
        ) : (
          <div className="tl">
            {logs.map((l: any) => {
              const meta = ACTION_META[l.action] ?? { icon: "ti-point", bg: "var(--gray-pale)", fg: "var(--text2)" };
              const desc = (l.details && l.details.description) || l.action;
              const extras = Object.entries(l.details ?? {}).filter(([k, v]) => k !== "description" && v != null && v !== "");
              const dk = dayKey(l.createdAt);
              const showDay = dk !== lastDay;
              lastDay = dk;
              return (
                <div key={l.id}>
                  {showDay && <div className="daydiv">{dk}</div>}
                  <div className="ev">
                    <div className="dot" style={{ background: meta.bg, color: meta.fg }}><i className={`ti ${meta.icon}`} /></div>
                    <div className="card">
                      <div className="row1">
                        <span className="av">{initials(l.userName)}</span>
                        <span className="name">{l.userName ?? "System"}</span>
                        {l.roleName && <span className="role">{l.roleName}</span>}
                        <span className="time">{timeOf(l.createdAt)}</span>
                      </div>
                      <div className="act">{desc}</div>
                      {extras.length > 0 && (
                        <div className="detail">
                          {extras.map(([k, v]) => (
                            <span key={k}><span className="k">{DETAIL_LABELS[k] ?? k}:</span> {String(v)}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ height: 30 }} />
      </div>
    </div>
  );
}
